import { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  ShieldAlert,
  Search,
  School,
  User,
  GraduationCap,
  Calendar,
  Clock,
  Printer,
  FileCheck2,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import { api } from '../../api/client';
import { formatDateBR } from '../../utils/dateUtils';

interface DocumentoValidacao {
  tipo: string;
  codigoAutenticacao: string;
  dataEmissao: string;
  anoLetivo: number;
  situacaoMatricula: string;
  aluno: {
    id: string;
    nome: string;
    matricula: string;
    cpf: string;
    dataNascimento: string;
    turma: string;
    serie: string;
    turno: string;
  };
  escola: {
    id: string;
    nome: string;
    codigoINEP: string;
    endereco: string;
    cidade: string;
    uf: string;
    cep: string;
    telefone: string;
    email: string;
  };
  assinatura: {
    nomeDiretor: string;
    cpfDiretor: string;
    cargo: string;
  };
  seguranca: {
    hashDocumento: string;
    certificacao: string;
    validadeDias: number;
  };
}

interface ValidacaoResponse {
  sucesso: boolean;
  autentico: boolean;
  status: string;
  mensagem: string;
  documento?: DocumentoValidacao;
}

export function ValidarDocumento() {
  const [searchParams] = useSearchParams();
  const routeParams = useParams<{ codigo?: string }>();
  const navigate = useNavigate();

  const [codigoInput, setCodigoInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<ValidacaoResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const initialCodigo = routeParams.codigo || searchParams.get('codigo') || '';
  const initialToken = searchParams.get('token') || '';

  const executarValidacao = async (cod: string, tok?: string) => {
    if (!cod && !tok) return;
    setLoading(true);
    setErro(null);
    setResultado(null);

    try {
      const res = await api.get<ValidacaoResponse>(
        `/public/documentos/validar?codigo=${encodeURIComponent(cod)}&token=${encodeURIComponent(tok || '')}`,
      );
      setResultado(res.data);
      if (!res.data.autentico) {
        setErro(res.data.mensagem || 'Documento não encontrado ou inválido.');
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        'Não foi possível verificar a autenticidade do documento. Verifique o código e tente novamente.';
      setErro(msg);
      setResultado({
        sucesso: false,
        autentico: false,
        status: 'ERRO',
        mensagem: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialCodigo) {
      setCodigoInput(initialCodigo);
      executarValidacao(initialCodigo, initialToken);
    }
  }, [initialCodigo, initialToken]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!codigoInput.trim()) return;
    executarValidacao(codigoInput.trim());
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f8fafc',
        color: '#0f172a',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ═══ TOP NAVBAR INSTITUCIONAL ═══ */}
      <header
        style={{
          background: '#0f172a',
          color: '#ffffff',
          padding: '16px 24px',
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer',
          }}
          onClick={() => navigate('/')}
        >
          <img
            src="/icons/logo-shield_proximo.svg"
            alt="Aluno Conectado"
            style={{ width: 34, height: 34, filter: 'grayscale(100%) invert(1)' }}
          />
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.2px' }}>
              ALUNO CONECTADO
            </div>
            <div style={{ fontSize: '0.68rem', color: '#94a3b8', letterSpacing: '0.5px' }}>
              PORTAL DE VERIFICAÇÃO PÚBLICA DE DOCUMENTOS
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: '0.7rem',
              fontWeight: 700,
              background: 'rgba(255,255,255,0.1)',
              padding: '4px 10px',
              borderRadius: 20,
              color: '#e2e8f0',
            }}
          >
            ICP-Brasil / Padrão Oficial
          </span>
        </div>
      </header>

      {/* ═══ MAIN CONTENT ═══ */}
      <main
        style={{
          flex: 1,
          maxWidth: 820,
          width: '100%',
          margin: '0 auto',
          padding: '32px 20px 60px',
        }}
      >
        {/* Box de Instrução e Formulário de Busca */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: '28px 24px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Search size={18} color="#0f172a" />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
                Consultar Autenticidade de Documento
              </h1>
              <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                Digite o Código de Autenticação impresso no rodapé da declaração escolar.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260, position: 'relative' }}>
                <input
                  type="text"
                  value={codigoInput}
                  onChange={(e) => setCodigoInput(e.target.value.toUpperCase())}
                  placeholder="Ex: AC-2026-B037-0001-A4F1"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    fontFamily: 'monospace',
                    letterSpacing: '0.5px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    background: '#f8fafc',
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !codigoInput.trim()}
                style={{
                  background: '#0f172a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 24px',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  cursor: loading || !codigoInput.trim() ? 'not-allowed' : 'pointer',
                  opacity: loading || !codigoInput.trim() ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'background 0.2s',
                }}
              >
                {loading ? 'Verificando...' : 'Verificar Documento'}
                {!loading && <ArrowRight size={16} />}
              </button>
            </div>
          </form>
        </div>

        {/* ═══ RESULTADO: DOCUMENTO AUTÊNTICO ═══ */}
        {resultado?.autentico && resultado.documento && (
          <div
            style={{
              background: '#ffffff',
              borderRadius: 16,
              border: '2px solid #0f172a',
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              animation: 'fadeIn 0.3s ease-out',
            }}
          >
            {/* Faixa de Confirmação de Autenticidade */}
            <div
              style={{
                background: '#16a34a',
                color: '#ffffff',
                padding: '16px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <ShieldCheck size={28} />
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: 800, letterSpacing: '0.3px' }}>
                    DOCUMENTO AUTÊNTICO E VÁLIDO
                  </div>
                  <div style={{ fontSize: '0.72rem', opacity: 0.9 }}>
                    Assinado digitalmente pela instituição de ensino e verificado na base oficial
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => window.print()}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  border: '1px solid rgba(255,255,255,0.4)',
                  padding: '6px 14px',
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Printer size={14} /> Imprimir Comprovante
              </button>
            </div>

            {/* Corpo do Comprovante Oficial */}
            <div style={{ padding: '28px 24px' }}>
              {/* Header do Documento */}
              <div
                style={{
                  textAlign: 'center',
                  paddingBottom: 20,
                  borderBottom: '1px solid #e2e8f0',
                  marginBottom: 24,
                }}
              >
                <span
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    color: '#0f172a',
                    background: '#f1f5f9',
                    padding: '4px 10px',
                    borderRadius: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {resultado.documento.tipo}
                </span>
                <h2
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 800,
                    color: '#0f172a',
                    margin: '10px 0 4px',
                  }}
                >
                  {resultado.documento.escola.nome}
                </h2>
                <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  {resultado.documento.escola.codigoINEP && (
                    <span>Código INEP: {resultado.documento.escola.codigoINEP} • </span>
                  )}
                  <span>
                    {resultado.documento.escola.cidade} - {resultado.documento.escola.uf}
                  </span>
                </div>
              </div>

              {/* Grid de Informações Estruturadas */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: 20,
                  marginBottom: 24,
                }}
              >
                {/* Card Dados do Aluno */}
                <div
                  style={{
                    background: '#f8fafc',
                    borderRadius: 12,
                    padding: 16,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      color: '#0f172a',
                      marginBottom: 12,
                      borderBottom: '1px solid #e2e8f0',
                      paddingBottom: 8,
                    }}
                  >
                    <User size={16} /> DADOS DO ESTUDANTE
                  </div>
                  <div style={{ fontSize: '0.82rem', lineHeight: 1.6, color: '#334155' }}>
                    <div>
                      <strong>Nome:</strong> {resultado.documento.aluno.nome}
                    </div>
                    <div>
                      <strong>Matrícula:</strong> {resultado.documento.aluno.matricula}
                    </div>
                    <div>
                      <strong>CPF:</strong> {resultado.documento.aluno.cpf}
                    </div>
                    <div>
                      <strong>Nascimento:</strong> {formatDateBR(resultado.documento.aluno.dataNascimento)}
                    </div>
                    <div>
                      <strong>Situação:</strong>{' '}
                      <span style={{ color: '#16a34a', fontWeight: 700 }}>
                        {resultado.documento.situacaoMatricula}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Dados Acadêmicos */}
                <div
                  style={{
                    background: '#f8fafc',
                    borderRadius: 12,
                    padding: 16,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      color: '#0f172a',
                      marginBottom: 12,
                      borderBottom: '1px solid #e2e8f0',
                      paddingBottom: 8,
                    }}
                  >
                    <GraduationCap size={16} /> DADOS ACADÊMICOS
                  </div>
                  <div style={{ fontSize: '0.82rem', lineHeight: 1.6, color: '#334155' }}>
                    <div>
                      <strong>Turma:</strong> {resultado.documento.aluno.turma}
                    </div>
                    <div>
                      <strong>Série / Etapa:</strong> {resultado.documento.aluno.serie}
                    </div>
                    <div>
                      <strong>Turno:</strong>{' '}
                      <span
                        style={{
                          background: '#e0e7ff',
                          color: '#3730a3',
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: 4,
                        }}
                      >
                        {resultado.documento.aluno.turno}
                      </span>
                    </div>
                    <div>
                      <strong>Ano Letivo:</strong> {resultado.documento.anoLetivo}
                    </div>
                  </div>
                </div>
              </div>

              {/* Informações da Escola & Assinatura */}
              <div
                style={{
                  background: '#f8fafc',
                  borderRadius: 12,
                  padding: 16,
                  border: '1px solid #e2e8f0',
                  marginBottom: 24,
                  fontSize: '0.82rem',
                  lineHeight: 1.6,
                  color: '#334155',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: '#0f172a',
                    marginBottom: 10,
                    borderBottom: '1px solid #e2e8f0',
                    paddingBottom: 6,
                  }}
                >
                  <School size={16} /> INSTITUIÇÃO & ASSINATURA DA DIREÇÃO
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: 16,
                  }}
                >
                  <div>
                    <div>
                      <strong>Endereço:</strong> {resultado.documento.escola.endereco || '—'}
                    </div>
                    <div>
                      <strong>CEP:</strong> {resultado.documento.escola.cep || '—'} •{' '}
                      {resultado.documento.escola.cidade}/{resultado.documento.escola.uf}
                    </div>
                    {resultado.documento.escola.telefone && (
                      <div>
                        <strong>Telefone:</strong> {resultado.documento.escola.telefone}
                      </div>
                    )}
                    {resultado.documento.escola.email && (
                      <div>
                        <strong>E-mail:</strong> {resultado.documento.escola.email}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      borderLeft: '2px solid #e2e8f0',
                      paddingLeft: 16,
                    }}
                  >
                    <div>
                      <strong>Diretor(a) Responsável:</strong>{' '}
                      {resultado.documento.assinatura.nomeDiretor}
                    </div>
                    <div>
                      <strong>CPF do Diretor:</strong>{' '}
                      {resultado.documento.assinatura.cpfDiretor}
                    </div>
                    <div>
                      <strong>Cargo:</strong> {resultado.documento.assinatura.cargo}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bloco de Segurança e Chave Criptográfica */}
              <div
                style={{
                  border: '1px dashed #cbd5e1',
                  borderRadius: 12,
                  padding: '14px 18px',
                  background: '#ffffff',
                  fontSize: '0.74rem',
                  color: '#64748b',
                }}
              >
                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                  CERTIFICADO DIGITAL DE AUTENTICIDADE
                </div>
                <div style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                  <strong>Código de Autenticação:</strong>{' '}
                  {resultado.documento.codigoAutenticacao}
                </div>
                <div style={{ wordBreak: 'break-all', fontFamily: 'monospace', marginTop: 2 }}>
                  <strong>Hash SHA-256:</strong> {resultado.documento.seguranca.hashDocumento}
                </div>
                <div style={{ marginTop: 6 }}>
                  Documento emitido eletronicamente via Sistema Aluno Conectado em conformidade com as normas vigentes de escrituração escolar digital.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ RESULTADO: NÃO ENCONTRADO / ERRO ═══ */}
        {resultado && !resultado.autentico && (
          <div
            style={{
              background: '#ffffff',
              borderRadius: 16,
              border: '2px solid #ef4444',
              padding: 32,
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            }}
          >
            <ShieldAlert size={48} color="#ef4444" style={{ margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#991b1b', margin: '0 0 8px' }}>
              Documento Não Localizado ou Inválido
            </h2>
            <p style={{ fontSize: '0.88rem', color: '#475569', maxWidth: 500, margin: '0 auto 20px' }}>
              Não foi possível confirmar a autenticidade com o código informado:{' '}
              <strong style={{ fontFamily: 'monospace', color: '#0f172a' }}>{codigoInput}</strong>.
            </p>
            <div
              style={{
                background: '#fef2f2',
                borderRadius: 10,
                padding: 16,
                fontSize: '0.8rem',
                color: '#7f1d1d',
                textAlign: 'left',
                maxWidth: 500,
                margin: '0 auto',
              }}
            >
              <strong>Orientações de conferência:</strong>
              <ul style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.5 }}>
                <li>Verifique se o código foi digitado corretamente conforme o impresso no documento;</li>
                <li>Certifique-se de incluir a sigla "AC-" no início, caso conste no carimbo;</li>
                <li>Caso a dúvida persista, entre em contato diretamente com a secretaria da escola.</li>
              </ul>
            </div>
          </div>
        )}
      </main>

      {/* ═══ FOOTER ═══ */}
      <footer
        style={{
          borderTop: '1px solid #e2e8f0',
          background: '#ffffff',
          padding: '20px 24px',
          textAlign: 'center',
          fontSize: '0.75rem',
          color: '#94a3b8',
        }}
      >
        © 2026 Aluno Conectado • Sistema de Gestão Escolar e Segurança • Validação Pública de Documentos
      </footer>
    </div>
  );
}
